/**
 * Jobs: the rider's work, in one screen.
 *
 * A rider has at most one active delivery and a queue of offers to choose from.
 * Those were two routes, which meant the same work was reachable two ways and
 * accepting an offer moved the rider somewhere else. They are one screen now,
 * and which half renders is decided by whether a delivery is in flight:
 *
 *  - In flight: the route and the next hand-off step, because that step is what
 *    the rider has to do next.
 *  - Otherwise: the offers queue, because that is what they have to choose.
 *
 * The queue is capped. A rider's decision horizon is the next few offers, and
 * the ranking is re-run on every read; showing forty rows would only add scroll
 * to a decision made in seconds.
 */
import { useState } from 'react'
import { addressNavigationURL } from '../logistics/navigation'
import '../logistics/logistics.css'
import {
  MinimalAlert,
  MinimalBadge,
  MinimalButton,
  MinimalCard,
  MinimalEmptyState,
  MinimalInfoPanel,
  MinimalInput,
} from '@ovasabi/ui-minimal'
import { BoltIcon, CheckIcon, ClockIcon, PinIcon, RouteIcon } from '../../components/icons'
import { Money, PageHeader, Screen } from '../../components/chow'
import {
  useDeliverySnapshot,
  useOfferCandidateSnapshot,
  useOfferSnapshot,
  useRiderLocationSnapshot,
  useRiderProfileSnapshot,
} from '../../generated/prototypeRuntime'
import { commands, isLiveRuntime } from '../../lib/runtime'
import { formatScheduled } from '../../lib/scheduling'
import { useOfferActions } from './offerActions'
import {
  activeDelivery,
  getStore,
  number,
  pickupKm,
  routeLine,
  schedule,
  text,
  useRiderId,
  type Snapshot,
} from './records'
import { riderStyle as Style } from './riderStyle'

/** How far ahead the queue is offered. See the note above. */
const QUEUE_LIMIT = 20

const NEXT_STAGE: Record<string, { target: string; label: string }> = {
  pickup: { target: 'transit', label: 'Confirm pickup' },
  transit: { target: 'dropoff', label: 'Arrived at drop-off' },
  dropoff: { target: 'done', label: 'Complete delivery' },
}

const stageLabel = (stage: string): string => {
  if (stage === 'pickup') return 'Go to pickup'
  if (stage === 'transit') return 'Taking order to customer'
  if (stage === 'dropoff') return 'Confirm hand-off'
  if (stage === 'done') return 'Completed'
  return 'Delivery in progress'
}

const ActiveDelivery = ({ delivery }: { delivery: Record<string, unknown> }) => {
  const riderId = useRiderId()
  const locations = useRiderLocationSnapshot() as Snapshot
  const profiles = useRiderProfileSnapshot() as Snapshot
  const vehicle = text(profiles.records[0] ?? {}, ['vehicle_type'])
  const travelMode = vehicle === 'on_foot' ? 'walk' : vehicle === 'bicycle' ? 'bicycle' : vehicle === 'motorbike' ? 'two_wheeler' : 'drive'
  const [pin, setPin] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const id = text(delivery, ['id', 'delivery_id'])
  const stage = text(delivery, ['stage', 'status'], 'pickup').toLowerCase()
  const next = NEXT_STAGE[stage]

  // Proof is rejected by the server without coordinates, so they come from the
  // rider's own location record rather than from the delivery: the projection
  // deliberately withholds pickup and drop-off coordinates from the client.
  const location = locations.records[0] ?? {}
  const latitude = number(location, ['latitude'])
  const longitude = number(location, ['longitude'])
  const hasFix = Boolean(location.id) && latitude !== 0 && longitude !== 0

  const address = text(delivery, stage === 'pickup' ? ['pickup_address'] : ['dropoff_address'])
  const navigation = next && address.trim() && address.trim().length <= 256 ? addressNavigationURL(address, travelMode) : undefined

  const advance = async () => {
    if (!next || busy) return
    setBusy(true)
    setError('')
    try {
      if (isLiveRuntime()) {
        if (stage === 'pickup') {
          await commands.proof.confirmPickup({ delivery_id: id, rider_id: riderId, latitude, longitude })
        }
        if (stage === 'dropoff') {
          await commands.proof.confirmDropoff({
            delivery_id: id,
            rider_id: riderId,
            method: 'pin',
            customer_pin: pin,
            latitude,
            longitude,
          })
        }
        await commands.delivery.transition(id, { target_stage: next.target, reason: 'rider_app' })
      } else {
        void commands.delivery.transition(id, { target_stage: next.target, reason: 'rider_app' }).catch(() => undefined)
        getStore('delivery.deliveries')?.upsert({ ...delivery, stage: next.target, updated_at: new Date().toISOString() })
      }
      setPin('')
    } catch {
      setError('Keep the order safe, check your connection and try this step again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {error ? (
        <MinimalAlert tone="danger" title="We could not update this delivery">
          {error}
        </MinimalAlert>
      ) : null}
      {isLiveRuntime() && !hasFix ? (
        <MinimalAlert tone="warning" title="We have not received your location">
          Proof for this hand-off needs a location fix. Enable location access, then retry the step.
        </MinimalAlert>
      ) : null}
      <MinimalInfoPanel
        tone="info"
        icon={<RouteIcon />}
        eyebrow="Current step"
        title={stageLabel(stage)}
        description={formatScheduled(schedule(delivery))}
      />
      <MinimalCard padding="md">
        <Style.Stack>
          <Style.Top>
            <Style.Stack>
              <Style.Title>{text(delivery, ['merchant_display_name', 'restaurant_name'], 'Pickup')}</Style.Title>
              <Style.Meta>{routeLine(delivery)}</Style.Meta>
            </Style.Stack>
            <Money amount={number(delivery, ['pay_total_minor']) / 100} tone="brand" />
          </Style.Top>
          <Style.Facts>
            <Style.Fact><PinIcon size="sm" />{text(delivery, ['pickup_address'], 'Pickup details in route')}</Style.Fact>
            <Style.Fact><ClockIcon size="sm" />{stageLabel(stage)}</Style.Fact>
          </Style.Facts>
          {navigation ? <a href={navigation} target="_blank" rel="noopener noreferrer" className="logistics-navigation">Navigate to {stage === 'pickup' ? 'pickup' : 'customer'}</a> : null}
          {stage === 'dropoff' ? (
            <MinimalInput
              label="Customer PIN"
              description="Ask the customer for the four-digit PIN before completing the delivery."
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={pin}
              onChange={(event) => setPin(event.target.value.replace(/\D/g, ''))}
            />
          ) : null}
          {next ? (
            <Style.FullAction>
              <MinimalButton
                leading={<CheckIcon size="sm" />}
                loading={busy}
                disabled={busy || (stage === 'dropoff' && pin.length < 4)}
                onClick={advance}
              >
                {next.label}
              </MinimalButton>
            </Style.FullAction>
          ) : null}
        </Style.Stack>
      </MinimalCard>
    </>
  )
}

const OfferQueue = () => {
  const { busyId, notice, accept, decline } = useOfferActions()
  const offers = useOfferSnapshot() as Snapshot
  const candidates = useOfferCandidateSnapshot() as Snapshot
  // `offer.offers` is the pushed queue; the ranked candidates are the fallback
  // for a rider whose push lane has not delivered yet.
  const records = offers.records.length ? offers.records : candidates.records

  return (
    <>
      {notice === 'error' ? (
        <MinimalAlert tone="danger" title="That offer could not be updated">
          It may have expired or been accepted by another rider. Refresh and try again.
        </MinimalAlert>
      ) : notice === 'declined' ? (
        <MinimalAlert tone="info" title="Offer skipped">
          We will keep looking for a better match nearby.
        </MinimalAlert>
      ) : null}
      {records.length === 0 ? (
        <MinimalEmptyState
          icon={<BoltIcon />}
          title="You are caught up"
          description="Stay online and nearby offers will appear here as soon as they are ready."
        />
      ) : (
        <Style.List>
          {records.slice(0, QUEUE_LIMIT).map((record, index) => {
            const id = text(record, ['id', 'offer_id'], `offer-${index}`)
            const payMinor = number(record, ['pay_total_minor', 'rider_base_pay_minor'])
            const km = pickupKm(record)
            const when = schedule(record)
            return (
              <MinimalCard key={id} padding="md" hoverable>
                <Style.Stack>
                  <Style.Top>
                    <Style.Stack>
                      <Style.Title>{text(record, ['restaurant_name', 'merchant_name'], 'New pickup')}</Style.Title>
                      <Style.Meta>{routeLine(record)}</Style.Meta>
                    </Style.Stack>
                    <Money amount={payMinor / 100} tone="brand" />
                  </Style.Top>
                  <Style.Facts>
                    <Style.Fact><RouteIcon size="sm" />{km > 0 ? `${km.toFixed(1)} km to pickup` : 'Route ready'}</Style.Fact>
                    <Style.Fact><ClockIcon size="sm" />{number(record, ['eta_minutes'], 15)} min estimate</Style.Fact>
                  </Style.Facts>
                  <MinimalBadge tone={when ? 'info' : 'success'} size="sm">
                    {when ? formatScheduled(when) : 'Ready now'}
                  </MinimalBadge>
                  <Style.Actions>
                    <MinimalButton variant="secondary" disabled={Boolean(busyId)} onClick={() => decline(record)}>
                      Skip
                    </MinimalButton>
                    <MinimalButton loading={busyId === id} disabled={Boolean(busyId)} onClick={() => accept(record)}>
                      Accept offer
                    </MinimalButton>
                  </Style.Actions>
                </Style.Stack>
              </MinimalCard>
            )
          })}
        </Style.List>
      )}
    </>
  )
}

export const JobsPage = () => {
  const deliveries = useDeliverySnapshot() as Snapshot
  const active = activeDelivery(deliveries.records)
  // A scheduled job says so here rather than only in the step panel: whether a
  // delivery is immediate or booked ahead changes when the rider should start
  // towards it, which is the whole distinction a scheduled run turns on.
  const scheduled = active ? schedule(active) : undefined

  return (
    <Screen
      header={(
        <PageHeader
          title="Jobs"
          accentDot
        />
      )}
    >
      {active ? <ActiveDelivery delivery={active} /> : <OfferQueue />}
      {scheduled ? (
        <MinimalInfoPanel
          tone="info"
          icon={<ClockIcon />}
          eyebrow="Scheduled run"
          title={formatScheduled(scheduled)}
          description="Booked ahead rather than immediate. Collect within the window; the customer is expecting this drop."
        />
      ) : null}
    </Screen>
  )
}
